import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Compass, Crown, Heart, RefreshCw, Skull, Sparkles, Swords, Wand2, Zap
} from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

export const StoryDirectionModal = ({
  open,
  onOpenChange,
  directionType,
  setDirectionType,
  assistantLoading,
  assistantResults,
  setAssistantResults,
  handleStoryDirection,
  setGenerateOptions,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl flex flex-col bg-[#0F1629] border-violet-500/30">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2 text-white">
              <Compass className="w-5 h-5 text-violet-400" />
              Story Directions
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Click a mood to get AI-powered story direction suggestions
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4 py-4">
              {/* Mood Selection Grid */}
              <div>
                <Label className="text-slate-200 mb-3 block">Choose the mood for your next scene:</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { value: 'plot_twist', icon: '\u{1F504}', label: 'Plot Twist', desc: 'Shocking revelations', color: 'cyan' },
                    { value: 'dark_turn', icon: '\u{1F311}', label: 'Dark Turn', desc: 'Serious & heavy', color: 'slate' },
                    { value: 'happy', icon: '\u{2600}\u{FE0F}', label: 'Happy', desc: 'Uplifting & joyful', color: 'cyan' },
                    { value: 'emotional', icon: '\u{1F494}', label: 'Emotional', desc: 'Heart-wrenching', color: 'rose' },
                    { value: 'brutal', icon: '\u{2694}\u{FE0F}', label: 'Brutal', desc: 'Intense stakes', color: 'red' },
                    { value: 'mysterious', icon: '\u{1F52E}', label: 'Mysterious', desc: 'Secrets & intrigue', color: 'violet' },
                    { value: 'romantic', icon: '\u{1F495}', label: 'Romantic', desc: 'Love & connection', color: 'pink' },
                    { value: 'comedic', icon: '\u{1F604}', label: 'Comedic', desc: 'Humor & levity', color: 'emerald' },
                  ].map((mood) => (
                    <button
                      key={mood.value}
                      onClick={() => {
                        setDirectionType(mood.value);
                        handleStoryDirection(mood.value);
                      }}
                      disabled={assistantLoading}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        directionType === mood.value 
                          ? 'border-violet-400 bg-violet-500/20 shadow-lg shadow-violet-500/20' 
                          : 'border-slate-600 bg-slate-800/50 hover:border-violet-500/50 hover:bg-slate-700/50'
                      } ${assistantLoading && directionType === mood.value ? 'animate-pulse' : ''}`}
                      data-testid={`mood-${mood.value}`}
                    >
                      <div className="text-2xl mb-1">{mood.icon}</div>
                      <div className="font-semibold text-white text-sm">{mood.label}</div>
                      <div className="text-xs text-slate-400">{mood.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Loading State */}
              {assistantLoading && (
                <div className="flex items-center justify-center py-8 bg-slate-800/50 rounded-xl">
                  <RefreshCw className="w-6 h-6 animate-spin text-violet-400 mr-3" />
                  <span className="text-slate-300">Shweebum is crafting directions...</span>
                </div>
              )}
              
              {/* Results as Clickable Cards */}
              {assistantResults && !assistantLoading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-200">Pick a direction and click to use it:</p>
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={() => setAssistantResults('')}>
                      Clear
                    </Button>
                  </div>
                  <div className="prose prose-sm text-slate-300 whitespace-pre-wrap bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                    {assistantResults}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-violet-600 hover:bg-violet-500"
                      onClick={() => {
                        setGenerateOptions(prev => ({ ...prev, prompt: assistantResults }));
                        onOpenChange(false);
                        toast.success("Direction copied to generation prompt!");
                      }}
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Use This Direction
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      onClick={() => handleStoryDirection(directionType)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Get New Suggestions
                    </Button>
                  </div>
                </div>
              )}

              {/* Quick Action Buttons */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <Label className="text-slate-200 mb-2 block text-sm">Quick mood shortcuts:</Label>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => { setDirectionType('brutal'); handleStoryDirection('brutal'); }}>
                    I want DANGER
                  </Button>
                  <Button variant="outline" size="sm" className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10" onClick={() => { setDirectionType('plot_twist'); handleStoryDirection('plot_twist'); }}>
                    Surprise me with a TWIST
                  </Button>
                  <Button variant="outline" size="sm" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" onClick={() => { setDirectionType('happy'); handleStoryDirection('happy'); }}>
                    Something SOOTHING
                  </Button>
                  <Button variant="outline" size="sm" className="border-slate-500/40 text-slate-400 hover:bg-slate-500/10" onClick={() => { setDirectionType('dark_turn'); handleStoryDirection('dark_turn'); }}>
                    Go TERRIFYING
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
  );
};
